import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import Tooltip from '@dashboard/components/Tooltip';
import clsx from 'clsx';
import { ReceiptText } from 'lucide-react';
import Link from '@/dashboard/components/Link';
import {
  CancelChargeFormDialog,
  cancelOffReason,
} from '@dashboard/components/OrderPage/CancelChargeDialog';
import { formatPriceForInput } from '@/dashboard/services/formatting/price';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { formatPrice, formatPriceInput } from '../../../services/formatting/price';
import { calculateMaximumRefundAmount } from '../../../services/payments';
import { chargeKey } from '@dashboard/lib/queryKeys';

const RefundFooter = ({ onCancelInstead }) => {
  const { values } = useFormikContext();
  return (
    <FormSubmit isInline align={null}>
      {onCancelInstead ? (
        <Link className="self-center text-sm" onClick={() => onCancelInstead(values)}>
          Cancel booking instead
        </Link>
      ) : (
        <span />
      )}
      <FormSubmit.Button appearance="primary">Refund booking</FormSubmit.Button>
    </FormSubmit>
  );
};

const PaymentsBookingRefundDialog = ({ payment }) => {
  const [open, setOpen] = useState(false);
  const [cancelPrefill, setCancelPrefill] = useState(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const maximumRefundAmount = calculateMaximumRefundAmount(payment);

  const refundCharge = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${payment.seller}/charges/${payment._id}/refund`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

      toast({
        title: 'Booking refunded',
        type: 'success',
      });

      setOpen(false);
    },
  });

  const tooltipLabel =
    payment.status === 'refunded'
      ? 'This booking has been refunded'
      : maximumRefundAmount === 0
        ? 'Nothing to refund yet'
        : '';
  const description =
    payment.status === 'canceled' || payment.orderStatus === 'canceled'
      ? 'Issue a partial or full refund.'
      : 'Issue a partial or full refund. Refunding does not cancel a booking or void its tickets.';
  const canCancel = !cancelOffReason(payment, 'booking');

  if (!payment.stripePaymentIntentId) {
    return null;
  }

  return (
    <>
      <Tooltip triggerClassName="w-fit" label={tooltipLabel} side="right">
        <Link
          onClick={() => setOpen(true)}
          className={clsx('w-full flex gap-x-2 items-center no-underline', {
            'pointer-events-none opacity-50 cursor-not-allowed':
              payment.status === 'refunded' || maximumRefundAmount === 0,
          })}
        >
          <>
            <ReceiptText className="w-4 h-4" />
            Refund booking
          </>
        </Link>
      </Tooltip>

      <Dialog
        title="Refund booking"
        cancelButton={null}
        confirmButton={null}
        size="md"
        open={open}
        onCancel={() => setOpen(false)}
        description={description}
      >
        {() => (
          <Form
            showState={false}
            initialValues={{
              reason: '',
              refundReason: '',
              refundAmount: formatPriceForInput({
                amount: maximumRefundAmount,
                currency: payment.currency,
                style: 'decimal',
              }),
              sendRefundEmail: true,
            }}
            onSubmit={async values => {
              try {
                const data = await refundCharge.mutateAsync({
                  ...values,
                  refundAmount: formatPriceInput(values.refundAmount, payment.currency),
                });

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

              toast({
                title: 'Booking refunded',
                type: 'success',
              });

              setOpen(false);
            }}
            fields={() => [
              {
                name: 'refundAmount',
                label: `Refund amount`,
                tags: [
                  `${formatPrice({ amount: maximumRefundAmount, currency: payment.currency })} left to refund`,
                ],
                type: 'number',
                required: true,
              },
              {
                name: 'refundReason',
                label: 'Reason',
                type: 'select',
                required: true,
                placeholder: 'Select a reason',
                options: [
                  { value: 'duplicate', label: 'Duplicate' },
                  { value: 'fraudulent', label: 'Fraudulent' },
                  { value: 'requested_by_customer', label: 'Requested by customer' },
                ],
              },
              {
                name: 'reason',
                label: 'Note',
                tags: ['not shown to customer'],
                type: 'textarea',
              },
              {
                name: 'sendRefundEmail',
                label: 'Send refund email to customer',
                type: 'checkbox',
              },
            ]}
            formSubmit={
              <RefundFooter
                onCancelInstead={
                  canCancel
                    ? values => {
                        setOpen(false);
                        setCancelPrefill({
                          refundAmount: values.refundAmount,
                          refundReason: values.refundReason,
                          refundReasonNote: values.reason,
                        });
                      }
                    : undefined
                }
              />
            }
          />
        )}
      </Dialog>

      {cancelPrefill ? (
        <CancelChargeFormDialog
          payment={payment}
          noun="booking"
          open
          onOpenChange={next => !next && setCancelPrefill(null)}
          prefill={cancelPrefill}
        />
      ) : null}
    </>
  );
};

export default PaymentsBookingRefundDialog;
